"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SectionContainer = SectionContainer;
var jsx_runtime_1 = require("react/jsx-runtime");
// ================================================================
// v2/components/SectionContainer/index.tsx
// セクション見出し + 任意の「すべて」リンク + 任意のタブフィルタを
// まとめるレイアウト共通部品。EventDetail / LotteryDetail / ArtistTop
// などで横断利用する。
//
// - 見出し: size で切り替える (lg: 20px / md: 16px、いずれも W600) +
//   foreground.neutral.primary
// - 説明文 (description): 見出しの 8px 下。Figma の SectionTitle は見出しと
//   説明文で 1 ブロックなので、本文 (16px 下) ではなく見出し側に寄せる
// - 「すべて」リンク: foreground.semantic.info。文言は showAllLabel で受ける
//   (このパッケージは i18n を持たないため、表示文言はすべて props で渡す)
// - タブフィルタ (Figma: TicketList): 見出しの下に独立した行としてピル型で並べる。
//   タブは { value, label } で受け取り、onTabChange は value を返す
//   (表示文言で識別すると、翻訳が重複したときに取り違える)。
//   - アクティブ: 面 background.primary.default / 文字は pickFilledInk が面の
//     明度から決める + shadow.brand
//   - 非アクティブ: 面 background.neutral.default / 文字 foreground.neutral.hint
//     (枠線・影なし)
//   - いずれも radius.full / 高さ 28px のピル。
//   - アクティブ状態を色だけで表さないよう、role="tablist" / role="tab" /
//     aria-selected を付与する
//
// 色・影は theme.palette.v2 (useV2Palette) から取得 (ハードコード禁止)。
// ================================================================
var material_1 = require("@mui/material");
var theme_1 = require("../../theme");
var tokens_1 = require("../../tokens");
function SectionContainer(_a) {
    var title = _a.title, description = _a.description, _b = _a.size, size = _b === void 0 ? 'lg' : _b, startIcon = _a.startIcon, showAllHref = _a.showAllHref, showAllLabel = _a.showAllLabel, tabs = _a.tabs, activeTab = _a.activeTab, onTabChange = _a.onTabChange, children = _a.children;
    var p = (0, theme_1.useV2Palette)();
    var titleStyle = size === 'md' ? tokens_1.textStyles.subsectionTitle : tokens_1.textStyles.sectionTitle;
    var titleLineHeight = size === 'md' ? tokens_1.lineHeight.md : tokens_1.lineHeight.lg;
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: "".concat(tokens_1.spacing.md, "px"),
            alignSelf: 'stretch',
        }, children: [(0, jsx_runtime_1.jsxs)(material_1.Stack, { spacing: "".concat(tokens_1.spacing.sm, "px"), children: [(0, jsx_runtime_1.jsxs)(material_1.Stack, { direction: "row", alignItems: "center", 
                        // Figma SectionTitle: アイコン付き見出しのギャップは 4px
                        spacing: "".concat(startIcon ? tokens_1.spacing.xs : tokens_1.spacing.sm, "px"), sx: { minHeight: titleLineHeight }, children: [startIcon, (0, jsx_runtime_1.jsx)(material_1.Typography, { sx: __assign(__assign({ flexGrow: 1 }, titleStyle), { lineHeight: "".concat(titleLineHeight, "px"), color: p.foreground.neutral.primary }), children: title }), showAllHref && showAllLabel && (
                            // component="a" を直接書くと theme の LinkComponent (utoniq-app では
                            // react-router) に差し替わらず全画面リロードになる。MuiLink は
                            // defaultProps.component で差し替え済みなので Link を使う。
                            (0, jsx_runtime_1.jsx)(material_1.Link, { href: showAllHref, sx: __assign(__assign({}, tokens_1.textStyles.label), { color: p.foreground.semantic.info, textDecoration: 'none', flexShrink: 0 }), children: showAllLabel }))] }), description && ((0, jsx_runtime_1.jsx)(material_1.Typography, { component: "div", sx: __assign(__assign({}, tokens_1.textStyles.body), { lineHeight: "".concat(tokens_1.lineHeight.sm, "px"), color: p.foreground.neutral.secondary, whiteSpace: 'pre-wrap' }), children: description }))] }), !!(tabs === null || tabs === void 0 ? void 0 : tabs.length) && ((0, jsx_runtime_1.jsx)(material_1.Stack, { role: "tablist", direction: "row", alignItems: "center", spacing: "".concat(tokens_1.spacing.sm, "px"), sx: {
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }, children: tabs.map(function (tab) {
                    var isActive = tab.value === activeTab;
                    return ((0, jsx_runtime_1.jsx)(material_1.ButtonBase, { role: "tab", "aria-selected": isActive, onClick: function () { return onTabChange === null || onTabChange === void 0 ? void 0 : onTabChange(tab.value); }, sx: __assign(__assign({ flexShrink: 0, height: 28, px: "".concat(tokens_1.spacing.md - tokens_1.spacing.xs, "px"), borderRadius: "".concat(tokens_1.radius.full, "px") }, tokens_1.textStyles.label), { fontWeight: tokens_1.fontWeight.semiBold, 
                            // アクティブ面は primary (dark では #459CFF の淡い面)。
                            // 白固定にせず面の明度から文字色を決める
                            color: isActive
                                ? (0, theme_1.pickFilledInk)(p.background.primary.default, p)
                                : p.foreground.neutral.hint, 
                            // アクティブ: 塗り primary + ブランド影 / 非アクティブ: 面 neutral のみ
                            backgroundColor: isActive
                                ? p.background.primary.default
                                : p.background.neutral.default, border: 'none', boxShadow: isActive ? p.shadow.brand : 'none', transition: 'all 0.15s' }), children: tab.label }, tab.value));
                }) })), (0, jsx_runtime_1.jsx)(material_1.Box, { children: children })] }));
}
